import type { configType, callbackFntype, callbackEventType } from './types';
import {
	initDOMNodeWithOverlay,
	prepareCloseModalNode,
	sanityCheck,
	importFonts,
	getTime,
} from './helper';

const appDomain = 'https://onramp.money';
const path = '/main';


var startTime: any, endTime: any;

export class OnrampWebSDK {
	_appURL: string;
	_isVisible: boolean;
	_clientCallbacks: { [key: string]: callbackFntype } = {};
	config: configType;
	_domNodes!: {
		body: HTMLBodyElement | null;
		iframe: HTMLIFrameElement;
		overlay: HTMLDivElement;
		shadow: ShadowRoot;
		shadowHost: HTMLDivElement;
	};
	constructor(config: configType) {
		this._appURL = `${appDomain}${path}`;
		this._isVisible = false;
		this.config = config;
		importFonts();
	}


	_getURL() {
		let url = this._appURL;
		const {
			appId,
			walletAddress,
			coinCode,
			network,
			coinAmount,
			fiatAmount,
			merchantRecognitionId,
			paymentMethod,
			flowType = 1,
			authToken = '',
			assetDescription = '',
			assetImage = '',
			paymentAddress = '',
			fiatType,
		} = this.config;

		switch (flowType) {
			case 1:
				// onramp
				url += '/buy';
				break;
			case 2:
				// offramp
				url += '/sell';
				break;
			case 3:
				// checkout
				url += '/checkout';
				break;
			case 4:
				// swap flow
				url += '/swap';
				break;
			default:
				// onramp
				url += '/buy';
				break;
		}

		url += `?appId=${appId}&mode=overlay&origin=${window.location.origin}`;

		// params only applicable for onramp / checkout flow (NFT flow)
		if ([1, 3].includes(flowType)) {
			if (walletAddress) {
				url += `&walletAddress=${walletAddress}`;
			}
			if (paymentMethod) {
				url += `&paymentMethod=${paymentMethod}`;
			}
			if (fiatType) {
				url += `&fiatType=${fiatType}`;
			}
		}

		// params only applicable checkout flow (NFT flow)
		if ([3].includes(flowType)) {
			if (assetDescription) {
				url += `&assetDescription=${assetDescription}`;
			}
			if (assetImage) {
				url += `&assetImage=${assetImage}`;
			}
		}

		// params only applicable for offramp
		if ([2, 4].includes(flowType)) {
			if (authToken) {
				url += `&authToken=${authToken}`;
			}
		}

		// params only applicable for swap flow
		if ([4].includes(flowType)) {
			if (paymentAddress) {
				url += `&paymentAddress=${paymentAddress}`;
			}
		}

		// common params (onramp - offramp)
		if (coinCode) {
			url += `&coinCode=${coinCode}`;
		}
		if (network) {
			url += `&network=${network}`;
		}
		if (coinAmount) {
			url += `&coinAmount=${coinAmount}`;
		}
		if (fiatAmount) {
			url += `&fiatAmount=${fiatAmount}`;
		}
		if (merchantRecognitionId) {
			url += `&merchantRecognitionId=${merchantRecognitionId}`;
		}

		url += `&clientHeight=${window.innerHeight}`;
		return url;
	}

	_registerSDKEvents() {
		window.addEventListener('message', this._widgetMessageHandler);
		window.addEventListener('keydown', this._handleEscapeClick);
		window.addEventListener(
			'ONRAMP_WIDGET_CLOSE_REQUEST',
			this._widgetCloseRequestHandler
		);
		window.addEventListener(
			'ONRAMP_WIDGET_CLOSE_REQUEST_CONFIRMED',
			this._widgetCloseHandler
		);
		window.addEventListener(
			'ONRAMP_WIDGET_CLOSE_REQUEST_CANCELLED',
			this._widgetCloseCancelHandler
		);
		window.addEventListener(
			'ONRAMP_WIDGET_READY',
			this._widgetReadyHandler
		);
		window.addEventListener(
			'ONRAMP_WIDGET_FAILED',
			this._widgetFailedHandler
		);
	}

	_teardownSDKEvents() {
		window.removeEventListener('message', this._widgetMessageHandler);
		window.removeEventListener('keydown', this._handleEscapeClick);
		window.removeEventListener(
			'ONRAMP_WIDGET_CLOSE_REQUEST',
			this._widgetCloseRequestHandler
		);
		window.removeEventListener(
			'ONRAMP_WIDGET_CLOSE_REQUEST_CONFIRMED',
			this._widgetCloseHandler
		);
		window.removeEventListener(
			'ONRAMP_WIDGET_CLOSE_REQUEST_CANCELLED',
			this._widgetCloseCancelHandler
		);
		window.removeEventListener(
			'ONRAMP_WIDGET_READY',
			this._widgetReadyHandler
		);
		window.removeEventListener(
			'ONRAMP_WIDGET_FAILED',
			this._widgetFailedHandler
		);
	}

	_handleEscapeClick = (event: { key: string; keyCode: number }) => {
		const escKeyCode = 27;
		if (
			event.key === 'Escape' ||
			event.key === 'Esc' ||
			event.keyCode === escKeyCode
		) {
			const customEvent = new CustomEvent('WIDGET_CLOSE_REQUEST', {
				detail: { eventData: { isOnramp: true } },
			});
			window.dispatchEvent(customEvent);
		}
	};

	_widgetMessageHandler = (e: callbackEventType) => {
		if (e?.data?.eventData?.isOnramp) {
			const { eventData } = e.data;
			// TODO: add sanity check for event type
			const customEvent = new CustomEvent(eventData.type, {
				detail: eventData,
			});
			window.dispatchEvent(customEvent);

			// also calls any callbacks registered by sdk instance
			if (eventData?.type.startsWith('ONRAMP_WIDGET_TX_')) {
				this._clientCallbacks?.['TX_EVENTS']?.(eventData);
			}
			if (
				[
					'ONRAMP_WIDGET_CLOSE_REQUEST_CONFIRMED',
					'ONRAMP_WIDGET_READY',
					'ONRAMP_WIDGET_FAILED',
					'ONRAMP_WIDGET_CONTENT_COPIED',
				].includes(eventData?.type)
			) {
				this._clientCallbacks?.['WIDGET_EVENTS']?.(eventData);
			}
		} else {
			console.log('event received from === ', e?.origin, e);
		}
	};

	_widgetCloseRequestHandler = () => {
		const modal = this._domNodes?.overlay?.querySelector('.close-modal');
		if (!modal) {
			this._domNodes?.overlay?.appendChild(prepareCloseModalNode());
		}
	};

	_widgetCloseHandler = () => {
		this._teardownSDKEvents();
		this.close();
	};

	_widgetCloseCancelHandler = () => {
		const modal = this._domNodes?.overlay?.querySelector('.close-modal');
		if (modal) {
			modal.remove();
		}
	};

	_widgetReadyHandler = () => {
		this._domNodes?.overlay.querySelector('.loader-container')?.remove();
		this._domNodes?.iframe?.classList?.add('visible');
		endTime = getTime();
		if (startTime && endTime) {
			console.log(
				'Render time in seconds ~~ ',
				Number((endTime - startTime) / 1000).toPrecision(4)
			);
		}
	};

	_widgetFailedHandler = () => {
		// this._teardownSDKEvents();
		// this.close();
	};

	close = () => {
		if (this._isVisible) {
			this._domNodes?.shadowHost?.remove();
			this._isVisible = false;
		} else {
			throw new Error(
				'Widget is not rendered. Call show method to render the widget'
			);
		}
	};

	show = () => {
		startTime = getTime();
		const dataSanityCheck = sanityCheck(this.config);
		if (dataSanityCheck) {
			if (this._isVisible) {
				throw new Error(
					'Widget is already rendered. Call close method to clear it up'
				);
			}
			this._registerSDKEvents();
			this._domNodes = initDOMNodeWithOverlay(this._getURL());
			this._domNodes.body?.appendChild(this._domNodes?.shadowHost);
			this._isVisible = true;
			setTimeout(() => {
				this._widgetReadyHandler();
			}, 1000);
		} else {
			return dataSanityCheck;
		}
	};

	on = (eventType: string, callbackFn: callbackFntype) => {
		if (['TX_EVENTS', 'WIDGET_EVENTS'].includes(eventType)) {
			this._clientCallbacks[eventType] = callbackFn;
		}
	};
}
